import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, useMediaQueryMatchesOnMobile } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonState, ButtonVariant, FeedbackMessage, FeedbackMessageVariant, Group, GroupGutter, Heading, HeadingSize, IconName, Text, useIsMounted, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { customerQueries } from '@as-react/common-queries';
import { useQuery } from '@tanstack/react-query';
import CustomerApi from '../../api/customer/CustomerApi';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { getQuerystringByName } from '../../util/URLParamUtil';
import { HtmlInclude } from '../../components/common/html-include/HtmlInclude';
import { DELAY } from '../../constants/GeneralConstants';
import { AccountPreferenceItem } from '../../components/account/account-preferences/AccountPreferenceItem';
import { getHomepageUrl } from '@as-react/common-utils';
const EMAIL_NEWSLETTER_TYPE = 'email_newsletter';
const AccountUnsubscribeContainer = ({ label, showCancel, newsletters, }) => {
    const { defaultRequestParams, servicesEndpoint, root } = useAemConfig();
    const isMobile = useMediaQueryMatchesOnMobile();
    const { t } = useTranslation(I18N_NAMESPACES.account);
    const isMounted = useIsMounted();
    const [buttonState, setButtonState] = useState(ButtonState.INTERACTIVE);
    const [errorFeedback, setErrorFeedback] = useState('');
    const [uuid, setUuid] = useState('');
    const [newsletterType, setNewsletterType] = useState('');
    const [preferencesNewsletter, setPreferencesNewsletter] = useState({});
    const withSubnewsletters = (newsletters === null || newsletters === void 0 ? void 0 : newsletters.subNewsletters) && (newsletters === null || newsletters === void 0 ? void 0 : newsletters.subNewsletters.length) > 0;
    const getQueryParam = (name) => { var _a; return (_a = getQuerystringByName(window.location.search, name)) !== null && _a !== void 0 ? _a : ''; };
    const { getPreferencesNewsletter } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const { data: customerNewsletters } = useQuery(getPreferencesNewsletter({ customerUUID: uuid }, { enabled: Boolean(uuid) }));
    useEffect(() => {
        setUuid(getQueryParam('uuid'));
        setNewsletterType(getQueryParam('type'));
    }, [isMounted]);
    useEffect(() => {
        if (customerNewsletters) {
            setPreferencesNewsletter(customerNewsletters);
        }
    }, [customerNewsletters]);
    const unsubscribe = () => __awaiter(void 0, void 0, void 0, function* () {
        const email = getQueryParam('email');
        const uuid = getQueryParam('uuid');
        const idCustomer = getQueryParam('idCustomer');
        const type = getQueryParam('type');
        const fasciaId = getQueryParam('shop');
        setButtonState(ButtonState.LOADING);
        try {
            if (withSubnewsletters && newsletterType === EMAIL_NEWSLETTER_TYPE) {
                yield CustomerApi.unsubscribeNewsletterPreferences(servicesEndpoint, defaultRequestParams, uuid, preferencesNewsletter);
            }
            else {
                yield CustomerApi.unsubscribePreferences(servicesEndpoint, defaultRequestParams, email, idCustomer, fasciaId, uuid, type);
            }
            setErrorFeedback('');
            setButtonState(ButtonState.FINISHED);
            setTimeout(() => (window.location.href = getHomepageUrl(root)), DELAY.DELAY_3000);
        }
        catch (_a) {
            setButtonState(ButtonState.INTERACTIVE);
            setErrorFeedback(t('account.unsubscribe.error'));
        }
    });
    const onPreferenceChange = (preference, value) => {
        let preferences = {};
        if (preference === newsletters.type) {
            Object.keys(preferencesNewsletter).forEach(key => (preferences[key] = value));
        }
        else {
            preferences = Object.assign(Object.assign({}, preferencesNewsletter), { [`${newsletters.type}#${preference}`]: value });
        }
        setPreferencesNewsletter(preferences);
    };
    const accountUnsubscribeBtn = (_jsxs(_Fragment, { children: [errorFeedback && (_jsx(Box, { paddingBlockEnd: "4", children: _jsx(FeedbackMessage, { text: errorFeedback, variant: FeedbackMessageVariant.NEGATIVE }) })), _jsxs(Group, { inline: !isMobile, gutter: GroupGutter.LARGE, children: [_jsx(Button, { fill: true, state: buttonState, text: label || t('account.unsubscribe.confirm'), finisher: {
                            icon: IconName.CHECK,
                            text: t('account.unsubscribe.unsubscribed'),
                        }, onClick: unsubscribe }), showCancel && (_jsx(Button, { fill: true, variant: ButtonVariant.OUTLINED, text: t('account.unsubscribe.cancel'), href: getHomepageUrl(root) }))] })] }));
    if (withSubnewsletters && newsletterType === EMAIL_NEWSLETTER_TYPE) {
        return (_jsxs(Box, { paddingBlock: "6", children: [_jsx(AccountPreferenceItem, { preferenceType: newsletters.type, selected: Object.values(preferencesNewsletter).some(value => value === true), onChange: isSelected => onPreferenceChange(newsletters.type, isSelected), checkboxLabel: newsletters.checkboxLabel, preferenceText: newsletters.preferenceText }), _jsx(Box, { paddingBlock: "6", marginInlineStart: "10", children: newsletters.subNewsletters.map((subItem, index) => (_jsx(Box, { paddingBlockStart: index === 0 ? '0' : '3', children: _jsx(AccountPreferenceItem, { preferenceType: subItem.type, selected: preferencesNewsletter[`${newsletters.type}#${subItem.type}`], onChange: isSelected => onPreferenceChange(subItem.type, isSelected), checkboxLabel: subItem.title, preferenceText: subItem.description }) }, subItem.type))) }), _jsx(Box, { paddingBlockEnd: "4", children: _jsx(Text, { children: _jsx(HtmlInclude, { htmlFragment: t('account.unsubscribe.message') }) }) }), accountUnsubscribeBtn] }));
    }
    return (_jsx(Box, { paddingBlockEnd: { base: '10', md: '12', lg: '14' }, paddingBlockStart: { base: '6', md: '7' }, children: _jsxs(Box, { shadow: !isMobile ? 'lg' : undefined, backgroundColor: "quiet", marginInline: "-2", paddingInline: { base: '4', md: '12' }, paddingBlockEnd: { base: '4', md: '6', lg: '12' }, paddingBlockStart: { base: '6', md: '10' }, children: [_jsx(Box, { paddingBlockEnd: "8", children: _jsx(Heading, { size: HeadingSize.S, children: _jsx(HtmlInclude, { htmlFragment: t('account.unsubscribe.message') }) }) }), accountUnsubscribeBtn] }) }));
};
export default AccountUnsubscribeContainer;
